import { Component, OnInit, OnDestroy, Output, EventEmitter, Inject, DOCUMENT } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthenticationService } from '../../core/services/auth.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { OrganizationService } from '../../core/services/organization.service';
import { NotificationService, Notification } from '../../core/services/notification.service';
import { ConversationService } from '../../core/services/conversation.service';
import { WhatsAppAccountService, WhatsAppMessagingLimit } from '../../core/services/whatsapp-account.service';
import { TokenManagerService } from '../../core/services/token-manager.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { OrgContextService } from '../../core/services/org-context.service';
import { PlatformAdminService } from '../../core/services/platform-admin.service';
import { PushNotificationService, PushFailureReason } from '../../core/services/push-notification.service';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';
import { formatTemplateNameForDisplay } from '../../core/utils/template-name.utils';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../core/services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, map, filter, Subscription } from 'rxjs';
import { changesLayout } from 'src/app/store/layouts/layout.actions';
import { getLayoutMode } from 'src/app/store/layouts/layout.selector';
import { RootReducerState } from 'src/app/store';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SimplebarAngularModule } from 'simplebar-angular';
import { canSendFrom, tierLabel } from 'src/app/core/utils/whatsapp-phone.utils';
import { roleLabel } from 'src/app/core/models/user-role.model';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  imports: [CommonModule, RouterModule, TranslateModule, BsDropdownModule, SimplebarAngularModule]
})

/**
 * Topbar component
 */
export class TopbarComponent implements OnInit, OnDestroy {
  get planningCenterProfileUrl(): string {
    // Use pcoId from currentUser if available, otherwise fallback to a placeholder
    const id = this.currentUser?.providerData[0]?.uid || 'undefined';
    return `https://people.planningcenteronline.com/people/AC${id}`;
  }
  mode: any
  element: any;
  cookieValue: any;
  flagvalue: any;
  countryName: any;
  valueset: any;
  theme: any;
  layout: string;
  dataLayout$: Observable<string>;
  // Define layoutMode as a property

  // User profile data
  currentUser: any = null;
  userName: string = 'User';
  userEmail: string = '';
  userPhotoURL: string = 'assets/images/users/user-dummy-img.jpg';
  organizationName: string = '';
  organizationId: string = '';
  userRole: string = '';
  /** Support Staff (the role, not a Solenex support session): no settings links. */
  isConversationsOnly: boolean = false;

  // Notifications
  notifications: Notification[] = [];
  unreadCount: number = 0;
  notificationTab: 'all' | 'unread' | 'read' = 'unread';

  // Web push (per device)
  get pushSupported(): boolean {
    return this.pushService.supported() === true;
  }

  get pushEnabled(): boolean {
    return this.pushService.enabled();
  }

  get pushBusy(): boolean {
    return this.pushService.busy();
  }

  get pushBlocked(): boolean {
    return this.pushService.permission() === 'denied' || this.pushService.needsInstall();
  }

  get pushHint(): string {
    if (this.pushService.needsInstall()) {
      return 'Add Echo to your Home Screen to turn on alerts.';
    }
    if (this.pushService.permission() === 'denied') {
      return 'Blocked. Allow notifications for this site in your browser settings.';
    }
    return this.pushEnabled
      ? 'On for this device. Your browser must be running.'
      : 'Get alerted on this device when a message arrives.';
  }

  get filteredNotifications(): Notification[] {
    if (this.notificationTab === 'unread') {
      return this.notifications.filter(n => !n.isRead);
    } else if (this.notificationTab === 'read') {
      return this.notifications.filter(n => n.isRead);
    }
    return this.notifications;
  }

  // WhatsApp Messaging Limits
  messagingLimits: WhatsAppMessagingLimit[] = [];
  loadingLimits: boolean = false;
  private readonly limitWarningRatio = 0.2;

  // Planning Center sync status
  pcoSyncStatus: string | null = null;
  pcoSyncError: string | null = null;
  private pcoSyncSub: Subscription | null = null;

  // Tracked subscriptions — cleaned up on logout and destroy
  private notificationsSub?: Subscription;
  private unreadCountSub?: Subscription;
  private authStateSub?: Subscription;
  private tokenExpirySub?: Subscription;
  private routerEventsSub?: Subscription;
  private counterSubscriptions: Subscription[] = [];

  // Token status
  tokenExpired: boolean = false;
  isRefreshingToken: boolean = false;
  isLoggingOut: boolean = false;
  showLogoutOverlay: boolean = false;

  constructor(@Inject(DOCUMENT) private document: any, private router: Router, private authService: AuthenticationService,
    private analytics: AnalyticsService,
    private organizationService: OrganizationService,
    private notificationService: NotificationService,
    private conversationService: ConversationService,
    private whatsappService: WhatsAppAccountService,
    private tokenManager: TokenManagerService,
    private permissionsService: PermissionsService,
    private orgContext: OrgContextService,
    private platformAdmin: PlatformAdminService,
    private pushService: PushNotificationService,
    public languageService: LanguageService,
    public translate: TranslateService,
    public _cookiesService: CookieService, public store: Store<RootReducerState>) {

  }

  listLang: any = [
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
    { text: 'Spanish', flag: 'assets/images/flags/spain.jpg', lang: 'es' },
    { text: 'German', flag: 'assets/images/flags/germany.jpg', lang: 'de' },
    { text: 'Italian', flag: 'assets/images/flags/italy.jpg', lang: 'it' },
    { text: 'Russian', flag: 'assets/images/flags/russia.jpg', lang: 'ru' },
  ];

  openMobileMenu: boolean;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  /**
   * Whether to offer the Solenex console in the profile menu.
   *
   * A getter rather than a field, because `platformAdmin` is a constructor
   * parameter property and class fields can initialise before those are
   * assigned — which would read the signal off undefined.
   */
  /**
   * The Solenex platform console, which lives on its own origin now.
   *
   * Empty outside production, where no console is deployed; the template hides
   * the link rather than offering a dead one.
   */
  readonly adminConsoleUrl = environment.adminConsoleUrl;

  get isPlatformAdmin(): () => boolean {
    return this.platformAdmin.isPlatformAdmin;
  }

  ngOnInit() {
    // The claim drives a menu item, so it has to be read before the menu can
    // render. Cheap — it comes off the already-cached ID token.
    this.platformAdmin.loadState();

    // this.initialAppState = initialState;
    this.store.select('layout').subscribe((data) => {
      this.theme = data.DATA_LAYOUT;
    })
    this.openMobileMenu = false;
    this.element = document.documentElement;

    this.cookieValue = this._cookiesService.get('lang');
    const val = this.listLang.filter(x => x.lang === this.cookieValue);
    this.countryName = val.map(element => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) { this.valueset = 'assets/images/flags/us.jpg'; }
    } else {
      this.flagvalue = val.map(element => element.flag);
    }

    // Subscribe to Auth State (Firebase) instead of localStorage
    this.authStateSub = this.authService.authState$.subscribe(user => {
      if (user) {
        this.currentUser = user;
        this.loadUserProfile();
      } else {
        // User signed out — immediately tear down all Firestore listeners
        // to prevent permission-denied errors firing after logout.
        this.tearDownUserSubscriptions();
      }
    });

    // Monitor token expiry
    this.tokenExpirySub = this.tokenManager.tokenExpiry$.subscribe(expired => {
      this.tokenExpired = expired;
    });

    // Reload organization on navigation (e.g., after org setup completes)
    this.routerEventsSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.loadOrganizationName();
    });
  }

  private tearDownUserSubscriptions(): void {
    this.notificationsSub?.unsubscribe();
    this.notificationsSub = undefined;
    this.unreadCountSub?.unsubscribe();
    this.unreadCountSub = undefined;
    this.pcoSyncSub?.unsubscribe();
    this.pcoSyncSub = null;
    this.counterSubscriptions.forEach(s => s.unsubscribe());
    this.counterSubscriptions = [];
  }

  ngOnDestroy(): void {
    this.tearDownUserSubscriptions();
    this.authStateSub?.unsubscribe();
    this.tokenExpirySub?.unsubscribe();
    this.routerEventsSub?.unsubscribe();
  }

  loadUserProfile() {
    if (!this.currentUser) return;

    const userId = this.currentUser.uid;
    console.log('🔍 Loading user profile from Firestore for:', userId);

    // 1. Basic info from Auth state
    this.userEmail = this.currentUser.email || '';

    // 2. Fetch extended profile from Firestore (Source of Truth)
    this.organizationService.getUserProfile(userId).subscribe({
      next: (profile) => {
        if (profile) {
          console.log('🔍 Firestore Profile:', profile);
          this.userName = profile.displayName || this.currentUser.displayName || 'User';
          this.userPhotoURL = profile.photoURL || this.currentUser.photoURL || 'assets/images/users/user-dummy-img.jpg';
          this.organizationName = profile.organizationName || '';
          this.organizationId = profile.organizationId || '';

          if (this.organizationId) {
            this.loadWhatsAppMessagingLimits();
            // Re-register web push if this device already opted in. Never
            // prompts on its own — that only happens from the toggle.
            this.pushService.init(userId, this.organizationId)
              .catch(err => console.warn('Push init failed:', err));
          }
        }
      },
      error: (err) => console.error('Error loading user profile:', err)
    });

    // 3. Load user role
    this.loadUserRole();

    // 4. Load notifications
    this.loadNotifications();
  }

  loadNotifications() {
    const userId = this.currentUser?.uid;
    console.log('🔔 loadNotifications: userId =', userId);
    if (!userId) return;

    // Tear down any previous subscriptions before creating new ones
    // (loadNotifications may be called again if the user profile reloads)
    this.notificationsSub?.unsubscribe();
    this.unreadCountSub?.unsubscribe();

    // Subscribe to real-time notifications
    this.notificationsSub = this.notificationService.getUserNotifications(userId, 50).subscribe({
      next: (notifications) => {
        console.log('🔔 Received notifications:', notifications);

        // 1) Handle silent notifications (do not surface in UI)
        const silentNotifications = notifications.filter(n => (n as any).silent);
        if (silentNotifications.length > 0) {
          silentNotifications.forEach(sn => {
            // mark as read so it doesn't keep triggering
            if (sn.id && !sn.isRead) {
              this.notificationService.markAsRead(sn.id).catch(() => { /* ignore */ });

              // If this is a reauth_required / signout action, sign the user out
              if (!this.isLoggingOut && (sn.type === 'reauth_required' || sn.action === 'signout')) {
                this.isLoggingOut = true;
                console.warn('🔐 Silent reauth required notification received. Logging out user.');
                this.authService.logout().then(() => {
                  // Redirect to login page after logout
                  window.location.href = '/auth/login';
                }).catch(err => {
                  console.error('Error during silent logout:', err);
                  window.location.href = '/auth/login';
                });
              }

            }
          });
        }

        // 2) Build visible notifications list (exclude silent ones)
        const visible = notifications.filter(n => !(n as any).silent);

        // Enrich notifications with icon and iconColor from their type
        this.notifications = visible.map(notification => {
          const style = this.notificationService.getNotificationStyle(notification.type);
          return {
            ...notification,
            icon: style.icon,
            iconColor: style.iconColor
          };
        });

        console.log('🔔 Enriched notifications:', this.notifications);
      },
      error: (error) => {
        console.error('Error loading notifications:', error);
      }
    });

    // Subscribe to unread count
    this.unreadCountSub = this.notificationService.getUnreadCount(userId).subscribe({
      next: (count) => {
        console.log('🔔 Unread count:', count);
        this.unreadCount = count;
      },
      error: (error) => {
        console.error('Error loading unread count:', error);
      }
    });
  }

  /**
   * Turn web push on or off for this browser. Enabling has to run straight off
   * the click so the browser still counts it as a user gesture.
   */
  async togglePush(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const wantsOn = input.checked;

    if (!wantsOn) {
      const revoked = await this.pushService.disable();
      if (!revoked) {
        await Swal.fire({
          icon: 'warning',
          title: 'Could not turn alerts off',
          text: 'We could not reach the server to unregister this device, so alerts may keep arriving. Please check your connection and try again.'
        });
      }
      return;
    }

    const result = await this.pushService.enable();
    if (result.ok) return;

    // Put the switch back — nothing was enabled.
    input.checked = false;
    await this.showPushFailure(result.reason);
  }

  private async showPushFailure(reason?: PushFailureReason): Promise<void> {
    const messages: Record<PushFailureReason, { title: string; text: string }> = {
      'denied': {
        title: 'Notifications are blocked',
        text: 'Your browser has blocked notifications for Echo. Allow them in your browser\'s site settings, then try again.'
      },
      'ios-needs-install': {
        title: 'Add Echo to your Home Screen',
        text: 'iPhone and iPad only deliver notifications to installed apps. In Safari, tap Share then "Add to Home Screen", open Echo from there, and turn this on again.'
      },
      'unsupported': {
        title: 'Not supported here',
        text: 'This browser cannot receive push notifications. Try Chrome, Edge or Firefox on desktop.'
      },
      'not-configured': {
        title: 'Push is not set up yet',
        text: 'Push notifications have not been configured for this environment. Please contact support.'
      },
      'error': {
        title: 'Could not turn on notifications',
        text: 'Something went wrong while enabling notifications. Please try again.'
      }
    };

    const { title, text } = messages[reason ?? 'error'];
    await Swal.fire({ icon: 'warning', title, text });
  }

  get messagingLimitAlertsCount(): number {
    return this.messagingLimits.filter((limit) => this.isLimitAlert(limit)).length;
  }

  private isLimitAlert(limit: WhatsAppMessagingLimit): boolean {
    if (!limit || limit.messagesRemaining === -1) {
      return false;
    }

    const remaining = limit.messagesRemaining ?? 0;
    const limitValue = limit.limitValue ?? 0;

    if (limitValue <= 0) {
      return remaining <= 0;
    }

    return remaining <= Math.ceil(limitValue * this.limitWarningRatio);
  }

  async loadUserRole() {
    const userId = this.currentUser?.uid;
    if (!userId) return;

    try {
      const permissions = await this.permissionsService.getUserPermissions(userId);
      if (permissions) {
        this.userRole = roleLabel(permissions.role);
        this.isConversationsOnly = permissions.isConversationsOnly;
      }
    } catch (err) {
      console.error('Error loading user role:', err);
    }
  }

  onNotificationClick(notification: Notification) {
    // Mark as read
    if (notification.id && !notification.isRead) {
      this.notificationService.markAsRead(notification.id);
    }

    // Navigate to link if available. navigateByUrl (not navigate) because links
    // may carry a query string, e.g. /conversations?c=<conversationId>.
    if (notification.link) {
      this.router.navigateByUrl(notification.link);
    }
  }

  markAllNotificationsAsRead() {
    const userId = this.currentUser?.uid;
    if (!userId) return;

    this.notificationService.markAllAsRead(userId);

    // These notifications are about incoming WhatsApp messages, so leaving the
    // Conversations badge unread after clearing the tray reads as a bug.
    this.conversationService.markAllAsRead().catch(error => {
      console.error('Error marking conversations as read:', error);
    });
  }

  clearAllNotifications() {
    const userId = this.currentUser?.uid;
    if (!userId) return;

    if (confirm('Are you sure you want to clear all notifications? This action cannot be undone.')) {
      this.notificationService.clearAllNotifications(userId).then(() => {
        console.log('All notifications cleared successfully');
      }).catch(error => {
        console.error('Failed to clear notifications:', error);
        alert('Could not clear notifications. Please check your permissions.');
      });
    }
  }

  toggleNotificationReadStatus(notification: Notification, event: Event) {
    event.stopPropagation();
    if (!notification.id) return;

    if (notification.isRead) {
      this.notificationService.markAsUnread(notification.id);
    } else {
      this.notificationService.markAsRead(notification.id);
    }
  }

  deleteNotification(notification: Notification, event: Event) {
    event.stopPropagation();
    if (!notification.id) return;

    if (confirm('Are you sure you want to delete this notification?')) {
      this.notificationService.deleteNotification(notification.id);
    }
  }

  getRelativeTime(timestamp: any): string {
    return this.notificationService.getRelativeTime(timestamp);
  }

  formatNotificationText(text: string): string {
    if (!text) return text;
    return text.replace(/"([a-z0-9_]+)"/g, (_match, name) => `"${formatTemplateNameForDisplay(name)}"`);
  }

  loadOrganizationName() {
    const userId = this.currentUser?.uid;  // Use Firebase uid, not Planning Center id
    console.log('🔍 loadOrganizationName: userId (uid) =', userId);
    console.log('🔍 loadOrganizationName: currentUser =', this.currentUser);

    if (this.currentUser && userId) {
      console.log('🔍 Attempting to load organization for user:', userId);
      this.organizationService.getUserOrganization(userId).subscribe({
        next: (userOrg: any) => {
          console.log('🔍 getUserOrganization returned:', userOrg);
          if (userOrg) {
            this.organizationName = userOrg.organizationName || '';
            this.organizationId = userOrg.organizationId || '';
            console.log('🔍 Loaded organization name:', this.organizationName);
            console.log('🔍 Loaded organization ID:', this.organizationId);

            // Load WhatsApp messaging limits if organization is connected
            if (this.organizationId) {
              this.loadWhatsAppMessagingLimits();
              this.subscribeToPcoSyncStatus();
            }
          } else {
            console.log('🔍 No user organization document found');
            this.organizationName = '';
            this.organizationId = '';
          }
        },
        error: (error: any) => {
          // Permission errors are expected during initial login before Firebase settles
          // They resolve on page refresh, so we can safely ignore them
          if (error?.code === 'permission-denied') {
            console.log('🔍 Permission denied loading organization (expected during login, will be resolved on refresh)');
          } else {
            console.error('🔍 Error loading organization:', error);
          }
          this.organizationName = '';
          this.organizationId = '';
          this.pcoSyncStatus = null;
          this.pcoSyncError = null;
        }
      });
    } else {
      console.log('🔍 No current user or uid available');
    }
  }

  /** Whether this organisation is connected to Planning Center. */
  get hasPco(): boolean {
    return this.orgContext.hasPco();
  }

  get showPcoSyncStatus(): boolean {
    // A standalone organisation never syncs, so this would be false anyway —
    // the explicit check keeps a stale status from a previous session from
    // surfacing a Planning Center pill to an org that has no Planning Center.
    return this.hasPco &&
      (this.pcoSyncStatus === 'syncing' || this.pcoSyncStatus === 'error');
  }

  private subscribeToPcoSyncStatus(): void {
    if (!this.organizationId) {
      return;
    }

    if (this.pcoSyncSub) {
      this.pcoSyncSub.unsubscribe();
    }

    this.pcoSyncSub = this.organizationService.getOrganizationRealtime(this.organizationId).subscribe({
      next: (org) => {
        const data: any = org || {};
        this.pcoSyncStatus = data.syncStatus || null;
        this.pcoSyncError = data.syncError || null;
      },
      error: (error) => {
        console.error('Error loading PCO sync status:', error);
        this.pcoSyncStatus = null;
        this.pcoSyncError = null;
      }
    });
  }

  loadWhatsAppMessagingLimits() {
    if (!this.organizationId) return;

    // Use real-time listener to get messaging limits from Firestore
    this.loadingLimits = true;
    this.whatsappService.getWhatsAppAccountRealtime(this.organizationId).subscribe({
      next: (whatsappAccount) => {
        // Only show limits if account exists and is connected
        if (!whatsappAccount || whatsappAccount.status !== 'connected') {
          console.log('📱 WhatsApp account not connected, skipping limits display');
          this.messagingLimits = [];
          this.loadingLimits = false;
          return;
        }

        // Extract messaging limits from phone number data (no API call needed!)
        if (whatsappAccount.phoneNumbers) {
          this.messagingLimits = whatsappAccount.phoneNumbers
            .filter(phone => canSendFrom(phone)) // Only numbers we would actually send from
            .map(phone => ({
              phoneNumberId: phone.id,
              displayPhoneNumber: phone.displayPhoneNumber,
              verifiedName: phone.verifiedName,
              limitTier: phone.messagingLimitTier || 'UNKNOWN',
              limitValue: phone.messagingLimitValue || 0,
              messagesSent: 0,
              messagesRemaining: phone.messagingLimitValue || 0
            }));

          // Subscribe to messaging-tier usage for each phone. The limit is shared
          // across the business portfolio, so every number shows the same usage.
          this.counterSubscriptions.forEach(s => s.unsubscribe());
          this.counterSubscriptions = [];
          for (const limit of this.messagingLimits) {
            const sub = this.whatsappService
              .getTierUsage(this.organizationId, whatsappAccount.businessId || limit.phoneNumberId)
              .subscribe(sent => {
                limit.messagesSent = sent;
                limit.messagesRemaining = Math.max(0, limit.limitValue - sent);
              });
            this.counterSubscriptions.push(sub);
          }
        } else {
          this.messagingLimits = [];
        }
        this.loadingLimits = false;
      },
      error: (error) => {
        console.error('Error loading WhatsApp account (realtime):', error);
        this.messagingLimits = [];
        this.loadingLimits = false;
      }
    });
  }

  getFormattedLimit(limit: WhatsAppMessagingLimit): string {
    if (limit.limitValue === -1) {
      return 'Unlimited';
    }
    return limit.limitValue.toLocaleString();
  }

  /** Delegates to the shared helper — see core/utils/whatsapp-phone.utils.ts. */
  getFormattedTierName(tierName: string): string {
    return tierLabel(tierName);
  }

  trackByPhoneNumberId(index: number, limit: any): string {
    return limit.phoneNumberId;
  }

  trackByNotificationId(index: number, notification: any): string {
    return notification.id;
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  /**
   * Toggles the right sidebar
   */
  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Logout the user
   */
  async logout() {
    if (this.isLoggingOut) return; // Prevent multiple logout attempts

    try {
      this.isLoggingOut = true;
      this.showLogoutOverlay = true; // Show full-screen overlay

      // Track logout event
      this.analytics.logEvent('authentication', {
        action: 'user_logged_out',
        user_id: this.currentUser?.uid || 'unknown'
      });

      if (environment.defaultauth === 'firebase') {
        // Properly await the logout process to ensure all async operations complete
        await this.authService.logout();
      }
      // Only navigate after logout is completely finished
      this.router.navigate(['/auth/login']);
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if logout fails, still navigate to login page
      this.router.navigate(['/auth/login']);
    } finally {
      this.isLoggingOut = false;
      this.showLogoutOverlay = false; // Hide overlay
    }
  }

  /**
   * Fullscreen method
   */
  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement && !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        /* Firefox */
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        /* IE/Edge */
        this.document.msExitFullscreen();
      }
    }
  }

  /**
   * Manually refresh the authentication token
   */
  async refreshToken() {
    if (this.isRefreshingToken) return;

    try {
      this.isRefreshingToken = true;
      await this.tokenManager.refreshToken();
      console.log('Token refreshed successfully');
      // You might want to show a success message here
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // You might want to show an error message here
      await this.logout(); // Force logout if refresh fails - properly await it
    } finally {
      this.isRefreshingToken = false;
    }
  }

  changeLayout(layoutMode: string) {
    this.theme = layoutMode;
    this.store.dispatch(changesLayout({ layoutMode }));
    this.store.select(getLayoutMode).subscribe((layout) => {
      document.documentElement.setAttribute('data-layout', layout)
    })
  }

  navigateToSettings() {
    this.router.navigate(['/organization/settings']);
  }
}